import { useState, useEffect, useCallback } from 'react'
import { useVestigioService } from 'hooks/useVestigioService'
import { useExameIdentificacaoNecropapiloscopicaService } from 'hooks/useExameIdentificacaoNecropapiloscopicaService'

export const useResultadoNecropapiloscopico = ({ idExameRealizado, idExameSolicitado, setDisableBotaoAvancar }) => {
  const vestigioService = useVestigioService()
  const exameNecropapiloscopicoService = useExameIdentificacaoNecropapiloscopicaService()

  const [openAnalise, setOpenAnalise] = useState(false)
  const [processandoAnalise, setProcessandoAnalise] = useState(false)
  const [processandoConfirmacaoAnalise, setProcessandoConfirmacaoAnalise] = useState(false)
  const [vestigiosExtraidosDeCadaver, setVestigiosExtraidosDeCadaver] = useState([])
  const [processandoVestigiosExtraidosDeCadaver, setProcessandoVestigiosExtraidosDeCadaver] = useState(false)
  const [idVestigioSelecionado, setIdVestigioSelecionado] = useState(null)
  const [analiseNecropapiloscopica, setAnaliseNecropapiloscopica] = useState(null)

  // Emulated Form state for "resultado"
  const [formState, setFormState] = useState({
    values: { resultado: null },
  })

  const handleChangeNewValueByNameProperty = useCallback((event, newValue, property) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [property]: newValue,
      },
    }))
  }, [])

  const formAnaliseNecropapiloscopica = {
    formState,
    setFormState,
    handleChangeNewValueByNameProperty,
  }

  // Fetch vestigios
  const fetchVestigiosExtraidosDeCadaver = useCallback(async () => {
    try {
      const response = await vestigioService.listarVestigiosDeExameSolicitadoExtraidosDeCadaverAsync(
        idExameSolicitado,
        setProcessandoVestigiosExtraidosDeCadaver
      )
      setVestigiosExtraidosDeCadaver(response)
    } catch (error) {
      console.error('Failed to fetch vestigios', error)
    }
  }, [idExameSolicitado])

  // Fetch specific analysis
  const fetchAnaliseNecropapiloscopica = useCallback(async (idVestigio) => {
    try {
      const response = await exameNecropapiloscopicoService.listarAnaliseNecropapiloscopicaDeVestigioEmExameAsync(
        idVestigio,
        idExameRealizado,
        setProcessandoAnalise
      )
      setAnaliseNecropapiloscopica(response)
    } catch (error) {
      console.error('Failed to fetch analysis details', error)
    }
  }, [idExameRealizado])

  // Verify if a vestigio has already been analyzed in this exam
  const verificarVestigioAnalisado = useCallback(
    (rowData) => {
      let result = false
      if (rowData.anaNecPaps) {
        rowData.anaNecPaps.forEach((analise) => {
          if (analise.id_exame_necropapiloscopico === idExameRealizado) {
            result = true
          }
        })
      }
      return result
    },
    [idExameRealizado]
  )

  // Handlers
  const clickOpenVestigio = useCallback(
    async (rowData) => {
      await fetchAnaliseNecropapiloscopica(rowData.id)
      setIdVestigioSelecionado(rowData.id)
      setOpenAnalise(true)
    },
    [fetchAnaliseNecropapiloscopica]
  )

  const handleConfirmarAnalise = useCallback(async () => {
    try {
      if (analiseNecropapiloscopica?.id) {
        const dadosEdicao = {
          id_vestigio: idVestigioSelecionado,
          id_exame_necropapiloscopico: idExameRealizado,
          resultado: formState.values.resultado,
        }
        await exameNecropapiloscopicoService.editarAnaliseNecropapiloscopica(
          analiseNecropapiloscopica.id,
          dadosEdicao,
          setProcessandoConfirmacaoAnalise
        )
      } else {
        const dadosCadastro = {
          resultado: formState.values.resultado,
          id_vestigio: idVestigioSelecionado,
          id_exame_necropapiloscopico: idExameRealizado,
        }
        await exameNecropapiloscopicoService.cadastrarAnaliseNecropapiloscopica(
          dadosCadastro,
          setProcessandoConfirmacaoAnalise
        )
      }

      await fetchVestigiosExtraidosDeCadaver()
      setIdVestigioSelecionado(null)
      setOpenAnalise(false)
    } catch (error) {
      console.error('Failed to submit analysis', error)
    }
  }, [
    analiseNecropapiloscopica,
    idVestigioSelecionado,
    idExameRealizado,
    formState.values.resultado,
    fetchVestigiosExtraidosDeCadaver,
  ])

  useEffect(() => {
    fetchVestigiosExtraidosDeCadaver()
  }, [fetchVestigiosExtraidosDeCadaver])

  // Sync loaded analysis data with formState
  useEffect(() => {
    if (analiseNecropapiloscopica?.id) {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          resultado: analiseNecropapiloscopica.resultado,
        },
      }))
    } else {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          resultado: null,
        },
      }))
    }
  }, [analiseNecropapiloscopica])

  // Manage navigation next block
  useEffect(() => {
    let disable = false
    if (vestigiosExtraidosDeCadaver?.length) {
      vestigiosExtraidosDeCadaver.forEach((vestigio) => {
        if (!verificarVestigioAnalisado(vestigio)) {
          disable = true
        }
      })
    }
    setDisableBotaoAvancar(disable)
  }, [vestigiosExtraidosDeCadaver, verificarVestigioAnalisado, setDisableBotaoAvancar])

  return {
    openAnalise,
    setOpenAnalise,
    processandoAnalise,
    processandoConfirmacaoAnalise,
    vestigiosExtraidosDeCadaver,
    processandoVestigiosExtraidosDeCadaver,
    formAnaliseNecropapiloscopica,
    clickOpenVestigio,
    handleConfirmarAnalise,
    verificarVestigioAnalisado,
  }
}
